'use client';

import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createTRPCClient, httpBatchLink } from '@trpc/client';
import { createTRPCContext as createTRPCReact } from '@trpc/tanstack-react-query';
import { useState } from 'react';
import superjson from 'superjson';
import type { AppRouter } from '@/server/routers';
import { useAuthStore, refreshAccessToken } from '@/hooks/use-auth';
import { isAuthorFrame, bootDemoSession } from '@/lib/tours/author-frame';
import { recordTrpcError } from '@/lib/bug-reports/client-error-buffer';

import { getBaseUrl } from './shared';

export const { TRPCProvider, useTRPC } = createTRPCReact<AppRouter>();

export function TRPCReactProvider({ children }: { children: React.ReactNode }) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        // Every failed call lands in the in-memory buffer a bug report shows
        // the admin before sending (tech-spec-admin-bug-reports). Nothing is
        // sent anywhere from here.
        queryCache: new QueryCache({ onError: (error) => recordTrpcError(error) }),
        mutationCache: new MutationCache({ onError: (error) => recordTrpcError(error) }),
        defaultOptions: {
          queries: {
            staleTime: 30 * 1000,
            retry: (failureCount, error: unknown) => {
              // Don't retry on auth errors
              if (
                error &&
                typeof error === 'object' &&
                'data' in error
              ) {
                const data = (error as { data?: { code?: string } }).data;
                if (data?.code === 'UNAUTHORIZED') return false;
              }
              return failureCount < 3;
            },
          },
        },
      })
  );

  const [trpcClient] = useState(() =>
    createTRPCClient<AppRouter>({
      links: [
        httpBatchLink({
          url: `${getBaseUrl()}/api/trpc`,
          transformer: superjson,
          async headers() {
            const token = useAuthStore.getState().accessToken;
            if (token) {
              return { authorization: `Bearer ${token}` };
            }
            return {};
          },
          async fetch(url, options) {
            let response = await globalThis.fetch(url, options);

            // If 401, try refreshing the token and retry.
            if (response.status === 401) {
              // Tier 4 (F2): in the author iframe, NEVER call /api/auth/refresh —
              // it POSTs the shared admin refresh cookie and returns an ADMIN
              // token, silently reverting the iframe to admin data. Re-mint the
              // DEMO token instead (authenticated via the tour_author capability
              // cookie, R2-#8).
              let newToken: string | null = null;
              if (isAuthorFrame()) {
                const ok = await bootDemoSession();
                newToken = ok ? useAuthStore.getState().accessToken : null;
              } else {
                const wasExpired = useAuthStore.getState().sessionExpired;
                // Operation type — NOT arrival order — decides who waits for
                // re-auth (D22). `httpBatchLink` issues queries as GET and
                // mutations as POST. Gating on "first waiter wins" looked
                // reasonable but is backwards in practice: AdminShell polls
                // every 10s while a save is occasional, so the poll would win
                // the race and the user's save would be the one discarded.
                newToken = await refreshAccessToken({
                  waitForReauth: options?.method === 'POST',
                });
                // Reads that failed fast during the outage need repopulating.
                if (newToken && wasExpired) {
                  void queryClient.invalidateQueries();
                }
              }
              if (newToken) {
                const newOptions = {
                  ...options,
                  headers: {
                    ...Object.fromEntries(
                      new Headers(options?.headers).entries()
                    ),
                    authorization: `Bearer ${newToken}`,
                  },
                };
                response = await globalThis.fetch(url, newOptions);
              }
            }

            return response;
          },
        }),
      ],
    })
  );

  return (
    <QueryClientProvider client={queryClient}>
      <TRPCProvider trpcClient={trpcClient} queryClient={queryClient}>
        {children}
      </TRPCProvider>
    </QueryClientProvider>
  );
}
